/**
 * Intuit QuickBooks Payments — browser tokenization helpers.
 * Tokens API requires no OAuth; card data never needs to hit Jupiter for tokenize.
 */

/** Replace Intuit field-path errors with copy the user can act on. */
function humanizeIntuitMessage(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (/card\.number\s+is\s+invalid/i.test(raw)) {
    return "This card number is invalid. Please check it and try again.";
  }
  return raw;
}

/** Prefer human-readable Intuit errors (moreInfo → message → detail). */
export function formatIntuitError(data, fallbackText = "") {
  const first = Array.isArray(data)
    ? data[0]
    : Array.isArray(data?.errors)
      ? data.errors[0]
      : data?.error || null;

  if (first && typeof first === "object") {
    const more = String(first.moreInfo || "").trim();
    const msg = String(first.message || "").trim();
    const detail = String(first.detail || "").trim();
    // Prefer moreInfo when it adds clarity (e.g. Amex 4-digit CVC)
    if (more) return humanizeIntuitMessage(more);
    if (msg) return humanizeIntuitMessage(msg);
    if (detail) return humanizeIntuitMessage(detail);
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return humanizeIntuitMessage(data.message.trim());
  }

  const text = String(fallbackText || "").trim();
  if (text) {
    try {
      const parsed = JSON.parse(text);
      return formatIntuitError(parsed, "") || humanizeIntuitMessage(text);
    } catch {
      return humanizeIntuitMessage(text);
    }
  }
  return "";
}

function getIntuitEnv() {
  const raw = (process.env.REACT_APP_INTUIT_ENV || "sandbox")
    .trim()
    .toLowerCase();
  return raw === "production" || raw === "prod" ? "production" : "sandbox";
}

/** Base host for Payments API (sandbox vs production). */
export function getIntuitPaymentsHost() {
  return getIntuitEnv() === "production"
    ? "https://api.intuit.com"
    : "https://sandbox.api.intuit.com";
}

export function getIntuitTokensUrl() {
  return `${getIntuitPaymentsHost()}/quickbooks/v4/payments/tokens`;
}

/**
 * Create a one-time Intuit Payments token from card fields (browser → Intuit).
 * @returns {Promise<string>} token value
 */
export async function tokenizeIntuitCard({
  number,
  expMonth,
  expYear,
  cvc,
  name,
  postalCode,
  streetAddress,
  city,
  region,
  country = "US",
  isDefault = false,
}) {
  const digits = String(number || "").replace(/\D/g, "");
  const monthNum = Number(expMonth);
  let yearNum = Number(expYear);
  if (Number.isFinite(yearNum) && yearNum > 0 && yearNum < 100) {
    yearNum = 2000 + Math.trunc(yearNum);
  }
  if (!digits || digits.length < 13) {
    throw new Error("Enter a valid card number.");
  }
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
    throw new Error("Enter a valid expiration month.");
  }
  if (!Number.isFinite(yearNum) || yearNum < 2000) {
    throw new Error("Enter a valid expiration year.");
  }
  if (!cvc || String(cvc).replace(/\D/g, "").length < 3) {
    throw new Error("Enter a valid CVV.");
  }
  const year = String(Math.trunc(yearNum));
  const month = String(Math.trunc(monthNum)).padStart(2, "0");

  const zip = String(postalCode || "").trim();
  const address = {
    country: country || "US",
  };
  if (zip) address.postalCode = zip;
  if (streetAddress) address.streetAddress = String(streetAddress).trim();
  if (city) address.city = String(city).trim();
  if (region) address.region = String(region).trim();

  const body = {
    card: {
      number: digits,
      expMonth: month,
      expYear: year,
      cvc: String(cvc).replace(/\D/g, ""),
      name: (name || "Cardholder").trim(),
      default: Boolean(isDefault),
      address,
    },
  };

  let response;
  try {
    response = await fetch(getIntuitTokensUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error("Could not reach Intuit Payments. Check your network.");
  }

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(formatIntuitError(data, text) || "Could not tokenize card.");
  }

  const token =
    (typeof data === "string" ? data : null) ||
    data?.value ||
    data?.token ||
    data?.id ||
    null;

  if (!token) {
    throw new Error("Intuit did not return a payment token.");
  }
  return String(token);
}
